import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MaskService } from '../../services/mask.service';
import { AdminService } from '../../admin/admin.service';
import { UniversalSearchService } from '../../universal-search/services/universal-search.service';
import { RightBridgeApiService } from '../../services/right-bridge-api.service';
import { UnifiedFlowService } from '../unified.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { Observable, takeUntil, Subject } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-copy-case-modal',
  templateUrl: './copy-case-modal.component.html',
  styleUrls: ['./copy-case-modal.component.scss'],
  imports: [MatCheckboxModule, MatButtonModule, MatDialogModule],
})
export class CopyCaseModalComponent implements OnInit {
  @ViewChild('gridContainer', { read: ViewContainerRef })
  gridContainer!: ViewContainerRef;
  private dialogRef =
    inject<MatDialogRef<CopyCaseModalComponent>>(MatDialogRef);
  private msk = inject(MaskService);
  changeDetector = inject(ChangeDetectorRef);
  private usService = inject(UniversalSearchService);
  private adminService = inject(AdminService);
  private rbs = inject(RightBridgeApiService);
  private snacky = inject(MatSnackBar);
  private unfFlowSvc = inject(UnifiedFlowService);
  private ss = inject(SessionStorageService);
  data = inject(MAT_DIALOG_DATA);

  form: FormGroup;
  dollarMask;
  finalAssetsRemaining = 0;
  initialAmount = 0;
  available = 0;
  currentSelectedProfileID = null;
  gridConfig = {
    gridName: 'CopyCase',
    showGridFilters: true,
    showFilterManager: false,
    manualSearch: true,
    searchButtonText: 'Search Results',
    downloadGridButton: false,
    showGridColumnFilters: false,
  };
  currentStep = 0;
  loadedData = null;
  allItems = [];
  headerItems = [];
  mainItems = [];
  activeHeaders = [];
  subItems = [];
  profileId: string;
  hideBack = false;
  unsubscribe = new Subject();

  constructor() {
    this.dollarMask = this.msk.dollarMaskSpecs();
  }

  ngOnInit(): void {
    this.profileId = this.data?.profile;
    this.gridConfig['profileId'] = this.profileId;
    if (this.unfFlowSvc.copyFromCaseSearch()) {
      this.hideBack = true;
      this.unfFlowSvc.copyFromCaseSearch.set(false);
    } else {
      this.renderGrid();
    }

    if (this.unfFlowSvc.copyCaseId()) {
      this.currentStep = 1;
      this.currentSelectedProfileID = this.unfFlowSvc.copyCaseId();
      this.unfFlowSvc.copyCaseId.set(null);
      this.getSelectedCaseData();
    }
  }

  async renderGrid() {
    if (this.gridContainer) {
      this.gridContainer.clear();
    }

    const { RbGridComponent } =
      await import('../../shared-components/rb-grid/rb-grid.component');

    const gridRef = this.gridContainer.createComponent(RbGridComponent);

    gridRef.instance.gridConfig = this.gridConfig;
    gridRef.instance.getSetup = this.getGridSetup;
    gridRef.instance.getData = this.getGridData;
    gridRef.instance.selectedProfileIdOutput
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(ev => {
        this.setCurrentSelectedProfileId(ev);
      });
  }

  cancel(): void {
    this.ss.remove('CopyCaseFilters');
    this.dialogRef.close({ data: 'cancel' });
  }

  back(): void {
    if (this.currentStep !== 0) {
      if (this.currentStep === 1) {
        this.currentSelectedProfileID = null;
        this.allItems = [];
        this.headerItems = [];
        this.mainItems = [];
        this.activeHeaders = [];
        this.subItems = [];
        this.loadedData = null;
      }
      this.currentStep -= 1;
    }
  }

  next(): void {
    if (this.currentStep === 0 && !this.loadedData) {
      this.currentStep += 1;
      this.getSelectedCaseData();
    } else if (this.currentStep === 1) {
      this.submitForCopy();
    }
  }

  getGridSetup(): Observable<any> {
    return this.adminService.getUIConfigHeaders({
      uiconfig: 'shared-json',
      type: 'case-search',
      implementation: 'rb',
    });
  }

  getGridData(payload) {
    return this.usService.caseSearch(payload, 'application/json');
  }

  setCurrentSelectedProfileId(profileID: string): void {
    this.currentSelectedProfileID = profileID;
  }

  getSelectedCaseData(): void {
    this.rbs
      .getGroupProfile(this.currentSelectedProfileID, 'CopyScreen')
      .subscribe(resp => {
        this.loadedData = resp;
        Object.keys(this.loadedData).forEach(dataKey => {
          this.allItems.push({
            key: dataKey,
            checked: false,
            display: this.loadedData[dataKey].Display,
            groupName: this.loadedData[dataKey].GroupName,
            exclusions: this.loadedData[dataKey].Exclusions,
            parent: this.loadedData[dataKey].ParentSection,
            filterVar: this.loadedData[dataKey].FilterVar,
            filterVarSet: this.loadedData[dataKey].FilterVarSet,
            subSection: this.loadedData[dataKey].SubSection,
          });
        });
        Object.keys(this.loadedData).forEach(dataKey => {
          if (
            this.loadedData[dataKey].Level === 'Header' &&
            this.loadedData[dataKey].Active
          ) {
            this.headerItems.push({
              key: dataKey,
              checked: false,
              display: this.loadedData[dataKey].Display,
              groupName: this.loadedData[dataKey].GroupName,
              exclusions: this.loadedData[dataKey].Exclusions,
              parent: this.loadedData[dataKey].ParentSection,
              filterVar: this.loadedData[dataKey].FilterVar,
              filterVarSet: this.loadedData[dataKey].FilterVarSet,
              subSection: this.loadedData[dataKey].SubSection,
            });
          }
        });

        Object.keys(this.loadedData).forEach(dataKey => {
          if (
            this.loadedData[dataKey].Level === 'Main' &&
            this.loadedData[dataKey].Active &&
            this.loadedData[this.loadedData[dataKey].ParentSection].Active
          ) {
            this.mainItems.push({
              key: dataKey,
              checked: false,
              display: this.loadedData[dataKey].Display,
              groupName: this.loadedData[dataKey].GroupName,
              exclusions: this.loadedData[dataKey].Exclusions,
              parent: this.loadedData[dataKey].ParentSection,
              filterVar: this.loadedData[dataKey].FilterVar,
              filterVarSet: this.loadedData[dataKey].FilterVarSet,
              subSection: this.loadedData[dataKey].SubSection,
            });
          }
        });

        this.mainItems.forEach(section => {
          if (this.loadedData[section.subSection]) {
            this.loadedData[section.subSection].forEach(subSection => {
              subSection['key'] = section.subSection;
              subSection['checked'] = false;
            });
            const subItems = [
              ...this.subItems,
              ...this.loadedData[section.subSection],
            ];
            section['subItems'] = subItems;
          }
        });

        this.headerItems.forEach(header => {
          header['mainItems'] = this.mainItems.filter(
            main => main.parent === header.key
          );
        });
      });
  }

  partiallyCompleteHeader(headerIndex: number): boolean {
    const mainItems = this.headerItems[headerIndex].mainItems;
    const anyChecked = mainItems.some(main => main.checked);
    const allChecked = mainItems.every(main => main.checked);
    return anyChecked && !allChecked;
  }

  partiallyCompleteMain(headerIndex: number, mainIndex: number): boolean {
    const subItems =
      this.headerItems[headerIndex].mainItems[mainIndex].subItems;
    if (subItems) {
      const anyChecked = subItems.some(sub => sub.checked);
      const allChecked = subItems.every(sub => sub.checked);
      return anyChecked && !allChecked;
    }
    return false;
  }

  checkExclusions(i) {
    if (this.headerItems[i].exclusions) {
      this.headerItems[i].exclusions.forEach(ex => {
        const exItem = this.headerItems.find(header => header.groupName === ex);
        exItem.checked = !exItem.checked;
      });
    }

    this.headerItems[i].mainItems.forEach(mainItem => {
      if (mainItem.exclusions) {
        mainItem.exclusions.forEach(ex => {
          const exItem = this.headerItems[i].mainItems.find(
            main => main.groupName === ex
          );
          exItem.checked = !exItem.checked;
        });
      }

      if (mainItem.subItems) {
        mainItem.subItems.forEach(subItem => {
          if (subItem.exclusions) {
            subItem.exclusions.forEach(ex => {
              const exItem = mainItem.subItems.find(
                item => item.groupName === ex
              );
              exItem.checked = !exItem.checked;
            });
          }
        });
      }
    });
  }

  update(checked: boolean, i: number, j?: number, k?: number): void {
    // If updating a header-level checkbox
    if (j === undefined) {
      this.headerItems[i].checked = checked;

      // Propagate the state to all mainItems and subItems
      const checkedGroupNames = new Set<string>(); // Track checked group names

      this.headerItems[i].mainItems.forEach(mainItem => {
        // Check exclusions for mainItems
        if (checked && mainItem.exclusions) {
          const hasExclusion = mainItem.exclusions.some(exclusion =>
            checkedGroupNames.has(exclusion)
          );
          if (hasExclusion) {
            mainItem.checked = false;
            return; // Skip this item
          }
        }

        mainItem.checked = checked;

        // Add to checkedGroupNames if checked
        if (checked && mainItem.groupName) {
          checkedGroupNames.add(mainItem.groupName);
        }

        if (mainItem.subItems) {
          mainItem.subItems.forEach(subItem => {
            // Check exclusions for subItems
            if (checked && subItem.exclusions) {
              const hasExclusion = subItem.exclusions.some(exclusion =>
                checkedGroupNames.has(exclusion)
              );
              if (hasExclusion) {
                subItem.checked = false;
                return; // Skip this item
              }
            }

            subItem.checked = checked;

            // Add to checkedGroupNames if checked
            if (checked && subItem.groupName) {
              checkedGroupNames.add(subItem.groupName);
            }
          });
        }
      });
    }

    // If updating a mainItem-level checkbox
    if (j !== undefined && k === undefined) {
      this.headerItems[i].mainItems[j].checked = checked;

      // Handle exclusions for mainItems
      if (this.headerItems[i].mainItems[j].exclusions) {
        this.headerItems[i].mainItems[j].exclusions.forEach(ex => {
          this.headerItems[i].mainItems.forEach((main, index) => {
            if (index !== j && main.groupName === ex) {
              this.uncheckItemAndChildren(main); // Uncheck excluded items and their children
            }
          });
        });
      }

      // Propagate the state to all subItems
      if (this.headerItems[i].mainItems[j].subItems) {
        const checkedGroupNames = new Set<string>(); // Track checked group names for subItems
        this.headerItems[i].mainItems[j].subItems.forEach(subItem => {
          // Check exclusions for subItems
          if (checked && subItem.exclusions) {
            const hasExclusion = subItem.exclusions.some(exclusion =>
              checkedGroupNames.has(exclusion)
            );
            if (hasExclusion) {
              subItem.checked = false;
              return; // Skip this item
            }
          }

          subItem.checked = checked;

          // Add to checkedGroupNames if checked
          if (checked && subItem.groupName) {
            checkedGroupNames.add(subItem.groupName);
          }
        });
      }

      // Update the header based on the state of all mainItems
      this.updateParentState(i);
    }

    // If updating a subItem-level checkbox
    if (j !== undefined && k !== undefined) {
      this.headerItems[i].mainItems[j].subItems[k].checked = checked;

      // Handle exclusions for subItems
      if (this.headerItems[i].mainItems[j].subItems[k].exclusions) {
        this.headerItems[i].mainItems[j].subItems[k].exclusions.forEach(ex => {
          this.headerItems[i].mainItems[j].subItems.forEach((sub, index) => {
            if (index !== k && sub.groupName === ex) {
              this.uncheckItemAndChildren(sub); // Uncheck excluded sub-items and their children
            }
          });
        });
      }

      // Update the mainItem based on the state of its subItems
      this.updateParentStateForMain(i, j);

      // Update the header based on the state of all mainItems
      this.updateParentState(i);

      // Handle exclusions at the parent level if the mainItem becomes checked
      if (checked) {
        const parentItem = this.headerItems[i].mainItems[j];
        if (parentItem.exclusions) {
          parentItem.exclusions.forEach(ex => {
            this.headerItems[i].mainItems.forEach((main, index) => {
              if (main.groupName === ex) {
                this.uncheckItemAndChildren(main); // Uncheck excluded items and their children
              }
            });
          });
        }
      }
    }
  }

  // Helper function to uncheck an item and all its children
  uncheckItemAndChildren(item: any): void {
    item.checked = false;
    if (item.subItems) {
      item.subItems.forEach(subItem => {
        this.uncheckItemAndChildren(subItem); // Recursively uncheck sub-items
      });
    }
  }

  // Helper function to update the header state based on its mainItems
  updateParentState(headerIndex: number) {
    const mainItems = this.headerItems[headerIndex].mainItems;
    const anyChecked = mainItems.some(main => main.checked);
    this.headerItems[headerIndex].checked = anyChecked;
  }

  // Helper function to update the mainItem state based on its subItems
  updateParentStateForMain(headerIndex: number, mainIndex: number) {
    const subItems =
      this.headerItems[headerIndex].mainItems[mainIndex].subItems;
    const anyChecked = subItems.some(sub => sub.checked);
    this.headerItems[headerIndex].mainItems[mainIndex].checked = anyChecked;
  }

  getExclusionDisplay(exclusion: string, mainItems: any[]) {
    return mainItems.find(main => main.groupName === exclusion).display;
  }

  submitForCopy(): void {
    this.unfFlowSvc.setLoading(true);
    const checkedItems = this.parseCheckedItems();

    if (this.profileId) {
      this.rbs.selectiveCopyProfile(checkedItems).subscribe(resp => {
        if (resp.results.questionnaire) {
          this.snacky.open(
            `Case information was successfully imported. Please review each step to confirm and complete your recommendation.`,
            'Close',
            {
              duration: 6000,
            }
          );
          this.dialogRef.close({ data: resp.results.questionnaire });
        }
      });
    } else {
      this.dialogRef.close({ data: checkedItems });
    }
  }

  parseCheckedItems() {
    let checkedMains = [];
    this.headerItems.forEach(
      header =>
        (checkedMains = [
          ...header.mainItems.filter(main => main.checked),
          ...checkedMains,
        ])
    );

    const groups = [];

    checkedMains.forEach(main => {
      const parsedVar = {
        group: main.groupName,
      };

      if (main.filterVar) {
        parsedVar['filterVar'] = main.filterVar;
      }
      if (main.filterVarSet) {
        parsedVar['filterVarSet'] = main.filterVarSet;
      }

      if (main.subItems) {
        const checkedSubItems = main.subItems.filter(
          subItem => subItem.checked
        );
        const filterValue = checkedSubItems.map(subItem => subItem.FilterValue);
        parsedVar['filterValue'] = filterValue;
      }
      groups.push(parsedVar);
    });

    const finalVar = {
      groups: groups,
      sourceProfileId: this.currentSelectedProfileID,
      destinationProfileId: this.profileId,
      returnQuestionaire: true,
    };
    return finalVar;
  }
}
